import { splitUrl } from "@repo/fika-shared/src/lib/slug"
import { getLanguageLabel, type Locale } from "@repo/fika-shared/src/locale"
import { useNavigate, useParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { format } from "date-fns"
import {
  createEffect,
  createMemo,
  createSelector,
  createSignal,
  For,
  Show,
} from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Select } from "@/designSystem/components/select"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { type Pill, Pills } from "@/designSystem/patterns/pills"
import { PostPreview } from "@/designSystem/patterns/post-preview"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { graphql, type ResultOf, readFragment } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/get-title"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { useRelativeTime } from "@/lib/use-relative-time"
import { createBlogRouter } from "@/routes/posts-router"
import {
  postVersionContentClass,
  postVersionItemClass,
  postVersionsListClass,
  postVersionsTimelineClass,
  postVersionTimeClass,
  postVersionTimelineDotClass,
  postVersionTitleClass,
} from "./post-versions.css"

const PostVersionFragment = graphql(`
  fragment PostVersionFragment on PostVersion {
    id
    title
    createdAt
    description
    content
    imageHeaderUrl
    published
    sent
  }
`)

const PostVersionsQuery = graphql(
  `query PostVersions($id: String!, $locale: Locale) {
    post(id: $id) {
      id
      formId
      mainLocale
      archivedAt
      publication {
        id
        leadMagnetForms {
          id
          content
        }
      }
      currentVersion {
        title
      }
      currentVersions {
        id
        locale
      }
      versions(first: 100, locale: $locale) {
        edges {
          node {
            ...PostVersionFragment
          }
        }
      }
    }
  }`,
  [PostVersionFragment],
)

export function PostVersions() {
  const params = useParams()
  const id = createMemo(() => splitUrl(params.postId) || "")

  const [initialQueryResult] = createQuery({
    query: PostVersionsQuery,
    variables: createMemo(() => ({ id: id(), locale: null })),
    pause: () => !id(),
  })

  const initialPost = createMemo(() => initialQueryResult.data?.post)

  // Initialize selected locale to main locale once we have the data
  const [selectedLocale, setSelectedLocale] = createSignal<Locale | null>(null)

  createEffect(() => {
    const post = initialPost()
    if (post && selectedLocale() === null) {
      setSelectedLocale(post.mainLocale)
    }
  })

  const [postQueryResult] = createQuery({
    query: PostVersionsQuery,
    variables: createMemo(() => ({ id: id(), locale: selectedLocale() })),
    pause: () => !id() || selectedLocale() === null,
  })

  const post = createMemo(() => postQueryResult.data?.post || initialPost())
  const isLoading = createMemo(() => initialQueryResult.fetching && !post())

  return (
    <SafePostVersions
      post={post()}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      fetching={postQueryResult.fetching}
      isLoading={isLoading()}
    />
  )
}

function SafePostVersions(props: {
  post: ResultOf<typeof PostVersionsQuery>["post"] | undefined
  selectedLocale: () => string | null
  setSelectedLocale: (locale: string | null) => void
  fetching: boolean
  isLoading: boolean
}) {
  const { LL } = useI18nContext()
  const navigate = useNavigate()
  const title = createMemo(() => getTitle(props.post?.currentVersion?.title))

  const { paths } = createBlogRouter()
  const params = useParams()

  const postVersions = createMemo(() => {
    if (!props.post) return []
    return props.post.versions.edges.map(edge =>
      readFragment(PostVersionFragment, edge.node),
    )
  })

  const availableLocales = createMemo(() => {
    if (!props.post) return []
    const mainLocale = props.post.mainLocale
    const currentVersions = props.post.currentVersions || []

    const locales = currentVersions.map(v => v.locale)

    const options = locales.map(locale => {
      const isMain = locale === mainLocale
      return {
        value: locale,
        label: isMain
          ? `${getLanguageLabel(locale)} (Main)`
          : getLanguageLabel(locale),
        disabled: false,
      }
    })

    // Sort to put main locale first
    return options.sort((a, b) => {
      if (a.value === mainLocale) return -1
      if (b.value === mainLocale) return 1
      return a.label.localeCompare(b.label)
    })
  })

  const buttons = createMemo(() => {
    if (!props.post) return []
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post({
          id: props.post.id,
          title: props.post.currentVersion?.title ?? "",
        }).url,
      } as const,
    ]
  })

  const selectedVersionId = createMemo(() => {
    if (params.versionId) return params.versionId
    return postVersions()[0]?.id
  })

  const selectedPostVersion = createMemo(() => {
    return postVersions().find(v => v.id === selectedVersionId())
  })

  // Navigate to first version when versions load or locale changes
  createEffect(() => {
    if (!props.post) return
    const versions = postVersions()
    if (!params.versionId && versions && versions[0]) {
      navigate(
        paths()
          .post({
            id: props.post.id,
            title: props.post.currentVersion?.title ?? "",
          })
          .versions(versions[0].id),
        {
          replace: true,
        },
      )
    }
  })

  // When locale changes and current version is not in the list, navigate to first version
  createEffect(() => {
    if (!props.post) return
    const versions = postVersions()
    const currentVersionId = params.versionId

    if (currentVersionId && versions.length > 0) {
      const versionExists = versions.some(v => v.id === currentVersionId)
      if (!versionExists && versions[0]) {
        navigate(
          paths()
            .post({
              id: props.post.id,
              title: props.post.currentVersion?.title ?? "",
            })
            .versions(versions[0].id),
          {
            replace: true,
          },
        )
      }
    }
  })

  const postVersionId = createMemo(() => selectedPostVersion()?.id)
  const isSelected = createSelector(postVersionId)
  const selectedIsCurrent = createMemo(() => {
    if (!props.post) return false
    const selectedId = postVersionId()
    if (!selectedId) return false

    const currentVersionsIds = props.post.currentVersions?.map(v => v.id) ?? []
    return currentVersionsIds.includes(selectedId)
  })

  const RestorePostVersionMutation = graphql(
    `mutation RestorePostVersion($versionId: ID!) {
      restorePostVersion(versionId: $versionId) {
        ... on MutationRestorePostVersionSuccess {
          data {
            id
            currentVersion {
              id
              title
            }
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )

  const restorePostVersionMutation = createMutationWithErrorHandling(
    RestorePostVersionMutation,
    "restorePostVersion",
  )
  const [restoreState, restoreVersion] = restorePostVersionMutation

  async function handleRestoreVersion() {
    if (!props.post) return
    const version = selectedPostVersion()
    if (!version) return

    await restoreVersion(
      {
        versionId: version.id,
      },
      {
        onSuccess: () => {
          if (!props.post) return
          toast.success(LL().postVersions.restored())
          navigate(
            paths().post({
              id: props.post.id,
              title: props.post.currentVersion?.title ?? "",
            }).url,
          )
        },
      },
    )
  }

  const postVersionsLoading = createMemo(
    () => props.isLoading || props.fetching,
  )

  return (
    <MainLayout
      hue="amber"
      header={
        <NavigationWithButtons
          hue="amber"
          buttons={buttons()}
          title={LL().postVersions.title()}
        >
          <Button
            hue="amber"
            iconName="book-dashed"
            onClick={handleRestoreVersion}
            active={restoreState.fetching}
            disabled={!selectedPostVersion() || selectedIsCurrent()}
          >
            {LL().postVersions.restore()}
          </Button>
        </NavigationWithButtons>
      }
      rightPanel={
        <Show when={props.post}>
          {post => (
            <RightPanel
              closeHref={
                paths().post({
                  id: post().id,
                  title: post().currentVersion?.title ?? "",
                }).url
              }
              header={{
                type: "title",
                title: LL().postVersions.title(),
              }}
            >
              <Box gap="12" background="sand100" padding="12" fill>
                <Show when={availableLocales().length > 1}>
                  <Select
                    hue="amber"
                    label={LL().postVersions.language()}
                    options={availableLocales()}
                    value={() => props.selectedLocale()}
                    onChange={(value: string | null) =>
                      props.setSelectedLocale(value)
                    }
                  />
                </Show>
                <Show
                  when={!postVersionsLoading() && postVersions().length > 0}
                  fallback={
                    <Show
                      when={postVersionsLoading()}
                      fallback={
                        <Box padding="12">
                          <Text color="rose700">
                            {LL().postVersions.failed()}
                          </Text>
                        </Box>
                      }
                    >
                      <Box padding="12">
                        <Text color="sand700">
                          {LL().postVersions.loading()}
                        </Text>
                      </Box>
                    </Show>
                  }
                >
                  <div class={postVersionsListClass}>
                    <div class={postVersionsTimelineClass} />
                    <For each={postVersions()}>
                      {postVersion => (
                        <PostVersionComponent
                          postVersion={postVersion}
                          post={post()}
                          isSelected={isSelected(postVersion.id)}
                        />
                      )}
                    </For>
                  </div>
                </Show>
              </Box>
            </RightPanel>
          )}
        </Show>
      }
    >
      <Show
        when={!props.isLoading && !props.fetching}
        fallback={
          <Box padding="24" gap="12">
            <Text color="sand700">{LL().postVersions.loading()}</Text>
          </Box>
        }
      >
        <Show
          when={selectedPostVersion()}
          fallback={
            <Box padding="24" gap="12">
              <Text color="rose700">{LL().postVersions.failedVersion()}</Text>
            </Box>
          }
        >
          {version => (
            <PostPreview
              post={{
                title: version().title,
                description: version().description,
                imageUrl: version().imageHeaderUrl,
                date: version().createdAt,
                content: version().content,
              }}
              defaultSubscribeFormId={props.post?.formId ?? null}
              loading={restoreState.fetching}
            />
          )}
        </Show>
      </Show>
    </MainLayout>
  )
}

type PostVersionType = ResultOf<typeof PostVersionFragment>

function PostVersionComponent(props: {
  postVersion: PostVersionType
  post: ResultOf<typeof PostVersionsQuery>["post"]
  isSelected: boolean
}) {
  const { paths } = createBlogRouter()
  const { LL } = useI18nContext()
  const date = useRelativeTime(() => props.postVersion.createdAt)
  const pills = createMemo(() => {
    const pills: Pill[] = []

    if (!props.post) return pills

    const currentVersionIds = props.post.currentVersions?.map(v => v.id) ?? []
    if (currentVersionIds.includes(props.postVersion.id)) {
      pills.push({
        icon: "star",
        label: LL().postVersions.pills.current(),
        hue: "amber",
      })
    }

    if (props.post.archivedAt) {
      pills.push({
        icon: "archive",
        label: LL().postVersions.pills.archived(),
        hue: "orange",
      })
    } else if (props.postVersion.sent) {
      pills.push({
        icon: "send",
        label: LL().postVersions.pills.sent(),
        hue: "indigo",
      })
    }

    if (props.postVersion.published) {
      pills.push({
        icon: "book-up",
        label: LL().postVersions.pills.published(),
        hue: "emerald",
      })
    }

    return pills
  })
  const formatDate = createMemo(() =>
    format(props.postVersion.createdAt, "PPp"),
  )

  return (
    <a
      href={
        props.isSelected
          ? undefined
          : paths()
              .post({
                id: props.post?.id ?? "",
                title: props.post?.currentVersion?.title ?? null,
              })
              .versions(props.postVersion.id)
      }
      class={postVersionItemClass({ selected: props.isSelected })}
    >
      <div class={postVersionTimelineDotClass} />
      <div class={postVersionContentClass}>
        <div class={postVersionTitleClass}>
          {getTitle(props.postVersion.title)}
        </div>
        <Pills pills={pills()} />
      </div>
      <time
        class={postVersionTimeClass}
        title={formatDate()}
        datetime={formatDate()}
      >
        {date()}
      </time>
    </a>
  )
}
